import React from 'react';
import styled from '@emotion/styled';
import {
  Layout,
  routes,
  breakpoints,
  pageTypes,
  siteName,
  siteDescription,
  normalize,
  AdBox,
  AdMobileAnchor,
  AdMobileBanner,
  AdVideo,
  AdLeaderboard
} from 'gatsby-theme-apollo';
import Footer from './footer';
import GameContent from './content';
import { VideoEmbed } from '../media/embed';

import Section from '../layout/section';
import MarkDownContent from '../posts/markdown-content';
import Tips from '../tips/tips';
import CTA from '../cta';
import Builds from '../entities/builds';
import LookAheadTable from '../layout/look-ahead-table';
import TierListPreviewCards from '../cards/tier-list-preview-card';
import ContentFeed from '../feeds/content-feed';
import { MainColumn, TwoColumn, SecondaryColumn } from '../layout/columns';
import { Flex } from '../layout/flex';

const Hero = styled.div`
  flex: 1;
  flex-shrink: 1;
  flex-grow: 0;
  display: flex;
  justify-content: center;
  padding: 15px;
`;

const CharacterHeroImage = styled.img`
  margin: auto auto;
  border-radius: 10px;
  max-height: 400px;
`;

const EntityDescription = styled.span`
  text-align: center;
  font-size: 18px;
  font-style: italic;
  width: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;
  display: block;
  padding: 10px;
  margin: 5px 5px;
`;

const Icon = styled.img({
  position: 'absolute',
  left: `4px`,
  width: '35px',
  top: `2px`,
  height: '35px',
  borderRadius: '25px',
  boxSizing: 'border-box',
  flexGrow: 0,
  flexShrink: 1,
});

const Abilities = styled.div({
  display: `flex`,
  justifyContent: `center`,
  alignItems: `flex-start`,
  width: `100%`,
  flexWrap: `wrap`
});

const Ability = styled.div({
  flexGrow: 1,
  display: `flex`,
  flexDirection: `row`,
  flexWrap: `wrap`,
  flex: 1,
  minWidth: `320px`,
  flexShrink: 1,
  alignItems: `flex-start`,
  marginBottom: `20px`,
  maxWidth: `100%`,
  margin: `10px`,
  [breakpoints.lg]: {
    maxWidth: `100%`
  }
});

const AbilityName = styled.h3`
  text-transform: uppercase;
  vertical-align: center;
  font-weight: bold;
  flex-shrink: 1;
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  margin-left: 10px;
  text-indent: 0px;
  margin-top: 5px;
  margin-bottom: 5px;
  box-sizing: border-box;
  font-family: ${props => props.theme.fontFamily};
  text-shadow: -1px 3px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;
`;

const AbilitySlot = styled.div`
  color: white;
  padding: 5px 10px;
  display: inline-block;
  display: flex;
  align-items: center;
`;

const AbilityDescription = styled.div`
  width: 100%;
  flex-shrink: 0;
  padding-top: 10px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
`;

const AbilityActivation = styled.div`
  color: rgba(255, 255, 255, 0.5);
  margin-top: 10px;
  padding: 2px 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const AbilityTop = styled.div`
  display: flex;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${props => props.theme.radius};
  align-items: center;
`;

const SecondaryName = styled.div`
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid ${props => props.theme.divider};
  margin: 20px;
  border-radius: ${props => props.theme.radius};
  display: inline-block;
  margin: 20px;
  padding: 2px 10px;
  font-size: 20px;
  text-align: center;
`;

const AbilityIcon = styled.img(props => ({
  flexGrow: `0`,
  width: `50px`,
  height: `50px`,
}));

const StatBox = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  padding: 10px;
  margin-bottom: 10px;
`;

const Stat = styled.div(props => ({
  position: `relative`,
  display: `flex`,
  flexDirection: `row`,
  flexWrap: `wrap`,
  margin: `3px`,
  padding: `5px`,
  justifyContent: `flex-start`,
  alignItems: `space-between`,
  boxSizing: `border-box`,
  borderRadius: `5px`,
  fontSize: `18px`,
  flex: 1,
  minWidth: `40%`,
  border: `1px solid #222`,
  flexGrow: 1,
  flexShrink: 0,
  backgroundColor: props.theme.backgroundDarkSemiTransparent,
  maxWidth: `300px`,
  [breakpoints.xs]: {
    maxWidth: `100%`
  }
}));

const StatValue = styled.span({
  display: `flex`,
  alignItems: `center`,
  textAlign: `left`,
  borderTopRightRadius: `5px`,
  borderBottomRightRadius: `5px`,
  padding: `4px 10px`,
  [breakpoints.sm]: {
    width: `100%`
  }
});

const StatTitle = styled.span(props => ({
  display: `flex`,
  alignItems: `center`,
  flexGrow: 1,
  marginLeft: props.marginLeft || '0px',
  padding: `0px 6px`,
  paddingLeft: '12px',
  paddingRight: `12px`,
  justifyContent: `flex-start`,
  fontWeight: `bold`,
  textAlign: `left`,
  [breakpoints.sm]: {
    width: `100%`
  }
}));

const EntityPage = ({ location, pageContext }) => {
  const pageType = pageTypes.ENTITY;
  const { gameName, entity, entityStatConfigs, entityType, relatedFeed } = pageContext;
  const { tips, abilities, recommendationsets, recommendationsetmemberships, guides } = entity;

  const { tierlists } = relatedFeed;

  const pageTitle = `${entity.name} | ${gameName} Wiki | ${siteName}`;
  const pageDescription = `Stats, guides, tips, and tricks lists, abilities, and ranks for ${entity &&
    entity.name}. ${siteDescription}`;

  const statBoxStats =
    entityStatConfigs &&
    entityStatConfigs.filter(stat => {
      return stat.statBox === true;
    });

  const crumbs = [
    { text: `${gameName} Wiki`, url: routes.GameHub(gameName), hide: true },
    { text: entityType.plural, url: routes.EntityHub(gameName, entityType.plural) }
  ];
  const entityStatsToDisplay = [];

  if (statBoxStats) {
    statBoxStats.forEach(statBoxStat => {
      const entityStatValue = entity.stats && entity.stats[statBoxStat.name];
      if (entityStatValue) {
        entityStatsToDisplay.push(entityStatValue);
      }
    });
  }

  let lookAheadLinks = [{ text: 'Tier Lists', href: routes.TierListHub(gameName) }];

  if (abilities && abilities.length > 0) {
    lookAheadLinks = lookAheadLinks.concat({
      text: 'Abilities',
      href: normalize.hyphenateAndLowerCase(`#${entity.name} Abilities`)
    });
  }

  lookAheadLinks = lookAheadLinks.concat([
    { text: 'Tips', href: normalize.hyphenateAndLowerCase(`#${entity.name} Tips`) },
    { text: 'Videos', href: normalize.hyphenateAndLowerCase(`#${entity.name} Videos`) }
  ]);

  return (
    <Layout
      location={location}
      title={pageTitle}
      description={pageDescription}
      pageType={pageType}
      gameName={gameName}
      pageProps={{ entityType: entityType && entityType.singular }}
    >
      <AdMobileAnchor placementId={'gh-anchor-' + pageType +'-bottom'} anchorType='bottom' offset='47' />
      <GameContent crumbs={crumbs} showTopShareBar>
        <TwoColumn>
          <MainColumn>
            <Section>
              <AdMobileBanner placementId={"gh-mobilebanner-" + pageType + "-top"} />
              <AdLeaderboard placementId={"gh-leaderboard-" + pageType + "-top"} />
              <Hero>
                <CharacterHeroImage src={entity.thumbnailURI} />
              </Hero>
              <Flex width="100%">
                <h1>{entity.name}</h1>
              </Flex>
              {entity.secondaryName && (
                <Flex style={{ width: `100%` }}>
                  <SecondaryName>{entity.secondaryName}</SecondaryName>
                </Flex>
              )}
            </Section>

            <LookAheadTable links={lookAheadLinks} />

            {entityStatsToDisplay && entityStatsToDisplay.length !== 0 && (
              <Section title={`${entity.name} Stats`}>
                <StatBox>
                  {statBoxStats &&
                    statBoxStats.map(stat => {
                      const statValue = entity.stats && entity.stats[stat.name];
                      const isArray = Array.isArray(statValue);

                      return (
                        entity.stats &&
                        entity.stats[stat.name] != null &&
                        !isArray && (
                          <Stat key={stat.displayName}>
                            <StatTitle marginLeft={stat.iconURI ? '25px' : '0px'}>
                              {stat.iconURI && stat.iconURI && <Icon src={stat.iconURI} />}
                              {stat.displayName}
                            </StatTitle>
                            <StatValue>{statValue}</StatValue>
                          </Stat>
                        )
                      );
                    })}
                </StatBox>
              </Section>
            )}

            {entity.stats && entity.stats.description && (
              <Section title="Overview" solidBackground>
                <EntityDescription>&quot;{entity.stats.description}&quot;</EntityDescription>
              </Section>
            )}

            {tierlists && tierlists.length !== 0 && (
              <Section
                title={`${entity.name}'s Rankings in Recent Tier Lists`}
                fullWidth
                transparent
              >
                <TierListPreviewCards tierListPreviews={tierlists} entityName={entity.name} />
              </Section>
            )}

            <AdBox placementId={"gh-box-" + pageType + "-mid"} />

            {abilities && abilities.length > 0 && (
              <Section title={`${entity.name} Abilities`} solidBackground>
                <Abilities>
                  {abilities.map(ability => {
                    return (
                      <Ability key={ability.name}>
                        <AbilityTop>
                          {ability.icon && ability.icon.url && (
                            <AbilityIcon src={ability.icon.url} alt={`${entity.name}'s ${ability.name}`} />
                          )}
                          <AbilityName>{ability.name}</AbilityName>
                          {ability.slot && <AbilitySlot>{ability.slot}</AbilitySlot>}
                        </AbilityTop>
                        {ability.activation && (
                          <AbilityActivation>{ability.activation}</AbilityActivation>
                        )}
                        <AbilityDescription>{ability.description}</AbilityDescription>
                      </Ability>
                    );
                  })}
                </Abilities>
              </Section>
            )}
            {entity.strengths && (
              <Section title="Strengths" solidBackground>
                <MarkDownContent
                  md={entity.strengths}
                  listItemIconURI="https://gamerhub-uploads.s3.us-west-1.amazonaws.com/e04a82f04fdf4de9abd6b5d747f428a2.png"
                />
              </Section>
            )}

            {entity.weaknesses && (
              <Section title="Weaknesses" solidBackground>
                <MarkDownContent
                  md={entity.weaknesses}
                  listItemIconURI="https://gamerhub-uploads.s3.us-west-1.amazonaws.com/58fbb4bea0ec4a9ebe4e2e6b5a2ba9e4.png"
                />
              </Section>
            )}
            {guides && guides.length > 0 && (
              <Section title={`${entity.name} Guide`} solidBackground>
                {guides.map((guide, index) => {
                  return <MarkDownContent md={guide.content} key={index} />;
                })}
              </Section>
            )}
            <Section solidBackground>
              <CTA
                campaignKey="MakeGuide"
                providedCampaignData={{ entityName: entity && entity.name }}
              />
              <CTA
                campaignKey="MakeTierList"
                providedCampaignData={{ entityName: entity && entity.name }}
              />
            </Section>
          </MainColumn>
          <SecondaryColumn>
            <Section title={`${entity.name} Tips`} solidBackground flushContent>
              <Tips entity={entity} tips={tips} onSubmitTip={() => {}} />
            </Section>
            {recommendationsetmemberships && recommendationsetmemberships.length > 0 && (
              <Section title="Used In Builds" solidBackground>
                <Builds gameName={gameName} builds={recommendationsetmemberships} />
              </Section>
            )}
            {recommendationsets && recommendationsets.length > 0 && (
              <Section title="Recommended Builds" solidBackground>
                <Builds gameName={gameName} builds={recommendationsets} />
              </Section>
            )}
            {(entity.videoGuideURI || entity.videos) && (
              <Section title={`${entity.name} Videos`} solidBackground>
                {entity.videoGuideURI && <VideoEmbed uri={entity.videoGuideURI} />}
                {entity.videos.map(({ externalID, title }, index) => {
                  return <VideoEmbed externalID={externalID} title={title} key={index}/>;
                })}
              </Section>
            )}
            <Section solidBackground>
              <ContentFeed feed={relatedFeed} pageContentID={entity.id} />
            </Section>
            <AdVideo placementId={'gh-video-' + pageType + '-floating'} />
          </SecondaryColumn>
        </TwoColumn>
        <Footer />
      </GameContent>
    </Layout>
  );
};

export default EntityPage;
